import React, { useState } from 'react';
import { Table, Modal, message, Tag, Input, Select } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { TTuroReservation } from '../start-work-order/type';
import { deleteAllTuroReservations } from '../../api/setting/turo-reservations';
import { TuroReservationCSVUpload } from '../cars/turo-reservation-csv-upload';
import { AutoTrackButton } from '../../components/button';
import { AutoTrackPagination } from '../../components/pagination';

type Props = {
    reservations: TTuroReservation[];
    total: number;
    page: number;
    pageSize: number;
    onChangePage: (page: number, pageSize?: number) => void;
    onReload: () => void;
    search: string;
    onSearch: (search: string) => void;
};

const WrapperTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: white;
    }
    .data-row:nth-child(odd) {
        background-color: #f7f8fa;
    }
    .ant-table-column-sorters .ant-table-column-sorter-inner {
        .anticon {
            font-size: 11px;
        }
    }
`;

const WrapperInput = styled.div`
    @media (max-width: 768px) {
        .ant-input-group-wrapper {
            width: 100% !important;
        }
    }
`;

const WrapperButton = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return `$${Number(value).toFixed(2)}`;
};

const formatDate = (value: string | null | undefined): string => {
    if (!value) return '-';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return value;
    }
};

export const TuroReservationPageList: React.FC<Props> = ({
    reservations,
    total,
    page,
    pageSize,
    onChangePage,
    onReload,
    search,
    onSearch,
}) => {
    const [openCSVModal, setOpenCSVModal] = useState(false);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [searchColumn, setSearchColumn] = useState('Guest');
    const [searchWord, setSearchWord] = useState(search);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const selectBeforeOptions = [
        { value: 'ReservationId', label: 'Reservation ID' },
        { value: 'Guest', label: 'Guest' },
        { value: 'Vehicle', label: 'Vehicle' },
        { value: 'VIN', label: 'VIN' },
        { value: 'TripStatus', label: 'Status' },
        { value: 'Location', label: 'Location' },
    ];

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        if (file) {
            setCsvFile(file);
            setOpenCSVModal(true);
        }
    };

    const triggerFileInput = () => {
        setCsvFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
    const [deletingAll, setDeletingAll] = useState(false);

    const onSearchData = React.useCallback(
        (word: string | undefined) => {
            onSearch((word ?? '').trim());
        },
        [onSearch]
    );

    const onSearchKeyword = React.useMemo(
        () => debounce(onSearchData, 600),
        [onSearchData]
    );

    const handleSearchData = (value: string | undefined) => {
        onSearchKeyword(value);
        setSearchWord(value ?? '');
    };

    React.useEffect(() => {
        setSearchWord(search);
    }, [search]);

    React.useEffect(() => {
        return () => {
            onSearchKeyword.cancel();
        };
    }, [onSearchKeyword]);

    const handleDeleteAll = async () => {
        if (deleteAllConfirmText !== 'delete') {
            message.error('Please type "delete" to confirm');
            return;
        }
        try {
            setDeletingAll(true);
            await deleteAllTuroReservations();
            message.success('All Turo reservations have been deleted');
            setShowDeleteAllModal(false);
            setDeleteAllConfirmText('');
            onReload();
        } catch (error: any) {
            message.error(
                error?.response?.data?.detail ||
                    'Failed to delete all reservations'
            );
        } finally {
            setDeletingAll(false);
        }
    };

    const columns = [
        {
            title: 'Reservation ID',
            dataIndex: 'reservationId',
            key: 'reservationId',
            width: 120,
            fixed: 'left' as const,
        },
        {
            title: 'Guest',
            dataIndex: 'guest',
            key: 'guest',
            width: 120,
        },
        {
            title: 'Vehicle',
            dataIndex: 'vehicleName',
            key: 'vehicleName',
            width: 180,
            ellipsis: true,
        },
        {
            title: 'VIN',
            dataIndex: 'vin',
            key: 'vin',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Trip Start',
            dataIndex: 'tripStart',
            key: 'tripStart',
            width: 160,
            render: (val: string) => formatDate(val),
        },
        {
            title: 'Trip End',
            dataIndex: 'tripEnd',
            key: 'tripEnd',
            width: 160,
            render: (val: string) => formatDate(val),
        },
        {
            title: 'Status',
            dataIndex: 'tripStatus',
            key: 'tripStatus',
            width: 100,
            render: (val: string) => {
                const color =
                    val === 'Completed'
                        ? 'green'
                        : val === 'Cancelled'
                          ? 'red'
                          : 'blue';
                return val ? <Tag color={color}>{val}</Tag> : '-';
            },
        },
        {
            title: 'Days',
            dataIndex: 'tripDays',
            key: 'tripDays',
            width: 70,
        },
        {
            title: 'Trip Price',
            dataIndex: 'tripPrice',
            key: 'tripPrice',
            width: 110,
            render: (val: number) => formatCurrency(val),
        },
        {
            title: 'Total Earnings',
            dataIndex: 'totalEarnings',
            key: 'totalEarnings',
            width: 130,
            render: (val: number) => (
                <span style={{ fontWeight: 600, color: '#52c41a' }}>
                    {formatCurrency(val)}
                </span>
            ),
        },
    ];

    return (
        <div className="flex flex-col w-full h-full">
            <div className="flex w-full px-[12px] py-[20px] shadow-e-03 items-center gap-3 justify-between bg-white rounded-[12px] mb-4 max-md:flex-col max-md:items-stretch">
                <div className="flex gap-3 items-center flex-1 min-w-0 max-md:min-w-0">
                    <WrapperInput className="flex items-center w-full gap-2">
                        <Input
                            placeholder="Search by ..."
                            value={searchWord}
                            onChange={e => handleSearchData(e.target.value)}
                            style={{
                                width: '420px',
                            }}
                            addonBefore={
                                <Select
                                    className="select-before"
                                    value={searchColumn}
                                    style={{ width: 150 }}
                                    onChange={(value: string) => {
                                        setSearchColumn(value);
                                        onSearchData(searchWord);
                                    }}
                                >
                                    {selectBeforeOptions.map(option => (
                                        <Select.Option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            }
                            size="large"
                        />
                    </WrapperInput>
                </div>
                <WrapperButton>
                    <AutoTrackButton
                        btnType="outline"
                        onClick={triggerFileInput}
                        style={{
                            minWidth: '160px',
                            borderColor: '#1890ff',
                            color: '#1890ff',
                        }}
                        btnSize="sm"
                    >
                        <div className="flex space-x-2 items-center">
                            <UploadOutlined />
                            <span>Import CSV</span>
                        </div>
                    </AutoTrackButton>
                    <AutoTrackButton
                        btnType="outline"
                        style={{
                            minWidth: '120px',
                            borderColor: '#ff4d4f',
                            color: '#ff4d4f',
                        }}
                        btnSize="sm"
                        onClick={() => setShowDeleteAllModal(true)}
                    >
                        <div className="flex space-x-2 items-center">
                            <DeleteOutlined />
                            <span>Delete All</span>
                        </div>
                    </AutoTrackButton>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept=".csv"
                    />
                </WrapperButton>
            </div>

            <div className="px-4 flex-1 overflow-auto">
                <WrapperTable>
                    <Table
                        className={classNames('white-header w-full')}
                        columns={columns}
                        dataSource={reservations.map((r, i) => ({
                            ...r,
                            key: r.TuroReservationId || i,
                        }))}
                        pagination={false}
                        scroll={{ x: 1500 }}
                        rowClassName="cursor-pointer data-row"
                    />
                </WrapperTable>
            </div>

            <div className="px-4 py-3">
                <AutoTrackPagination
                    current={page}
                    total={total}
                    pageSize={pageSize}
                    onChange={(page: number, pageSize?: number) =>
                        onChangePage(page, pageSize)
                    }
                />
            </div>

            <Modal
                centered
                open={openCSVModal}
                width={'100%'}
                title={
                    <div className="flex gap-2">
                        <span className="font-normal text-[18px]">
                            Turo Reservations CSV:
                        </span>
                        <span className="font-bold text-[18px] underline">
                            {csvFile?.name}
                        </span>
                    </div>
                }
                onCancel={() => setOpenCSVModal(false)}
                footer={null}
                afterOpenChange={(open: boolean) => {
                    if (!open) {
                        setCsvFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                    }
                }}
                destroyOnClose
            >
                <div className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
                    <TuroReservationCSVUpload
                        file={csvFile}
                        onClose={() => setOpenCSVModal(false)}
                        onImportComplete={() => onReload()}
                    />
                </div>
            </Modal>

            <Modal
                centered
                open={showDeleteAllModal}
                title={
                    <div className="font-bold text-[18px] text-red-600">
                        ⚠️ Delete All Turo Reservations
                    </div>
                }
                onCancel={() => {
                    setShowDeleteAllModal(false);
                    setDeleteAllConfirmText('');
                }}
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <AutoTrackButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => {
                                setShowDeleteAllModal(false);
                                setDeleteAllConfirmText('');
                            }}
                        >
                            Cancel
                        </AutoTrackButton>
                        <AutoTrackButton
                            btnType="danger"
                            btnSize="sm"
                            disabled={deleteAllConfirmText !== 'delete'}
                            loading={deletingAll}
                            onClick={handleDeleteAll}
                        >
                            Delete All
                        </AutoTrackButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <p>
                        This will permanently delete <strong>ALL</strong> Turo
                        reservations from the system. This action cannot be
                        undone.
                    </p>
                    <p>
                        Type <strong>delete</strong> below to confirm:
                    </p>
                    <Input
                        placeholder="Type 'delete' to confirm"
                        value={deleteAllConfirmText}
                        onChange={e => setDeleteAllConfirmText(e.target.value)}
                        status={
                            deleteAllConfirmText &&
                            deleteAllConfirmText !== 'delete'
                                ? 'error'
                                : undefined
                        }
                    />
                </div>
            </Modal>
        </div>
    );
};
